import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "@/lib/api";
import { Card } from "@/components/ui-kit";
import { Loader } from "@/components/Loader";
import { ArrowLeft, Navigation, Bell, Camera, Mic, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Permissions() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({})); }, []);

  const update = async (patch) => {
    setSettings((s) => ({ ...s, ...patch }));
    try { const { data } = await api.put("/settings", patch); setSettings(data); toast.success("Preferences saved"); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  if (!settings) return <Loader />;

  const perms = [
    { key: "location_permission_status", i: Navigation, t: "Location", d: "Requested only during an active emergency." },
    { key: "notification_enabled", i: Bell, t: "Notifications", d: "Alerts when a neighbour needs help.", bool: true },
    { key: "camera_permission_status", i: Camera, t: "Camera", d: "Optional — share a photo during an incident." },
    { key: "microphone_permission_status", i: Mic, t: "Microphone", d: "Optional — voice notes during an emergency." },
  ];

  const Toggle = ({ on, onClick, testId }) => (
    <button data-testid={testId} onClick={onClick} className={`w-14 h-8 rounded-full transition-colors relative shrink-0 ${on ? "bg-[#16A34A]" : "bg-[#111827]/15"}`}>
      <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-7" : "translate-x-1"}`} />
    </button>
  );

  return (
    <div className="ll-fade-up" data-testid="permissions-screen">
      <div className="px-5 pt-8 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/profile")} data-testid="perm-back" className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold font-head tracking-tight">Permissions</h1>
      </div>
      <div className="px-5 mt-3 space-y-3">
        <p className="text-sm text-[#111827]/70 bg-[#C4B5FD]/15 rounded-2xl p-4">HumNest does not continuously track you. Location is requested when you trigger or actively share an emergency.</p>
        {perms.map((p) => {
          const on = p.bool ? settings[p.key] : settings[p.key] === "granted";
          return (
            <Card key={p.key} className="flex items-center gap-3 p-5">
              <div className="w-11 h-11 rounded-xl bg-[#F97316]/10 flex items-center justify-center shrink-0"><p.i className="w-5 h-5 text-[#F97316]" /></div>
              <div className="flex-1"><p className="font-bold">{p.t}</p><p className="text-sm text-[#111827]/55">{p.d}</p></div>
              <Toggle on={on} testId={`perm-toggle-${p.key}`} onClick={() => update({ [p.key]: p.bool ? !on : (on ? "not_requested" : "granted") })} />
            </Card>
          );
        })}
        <Card className="flex items-center gap-3 p-5">
          <div className="w-11 h-11 rounded-xl bg-[#C4B5FD]/25 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-[#111827]/70" /></div>
          <div className="flex-1"><p className="font-bold">Emergency test mode</p><p className="text-sm text-[#111827]/55">Practice safely without alerting anyone.</p></div>
          <Toggle on={settings.emergency_test_mode} testId="perm-toggle-test-mode" onClick={() => update({ emergency_test_mode: !settings.emergency_test_mode })} />
        </Card>
      </div>
    </div>
  );
}
